<script setup lang="ts">
  import { ref } from 'vue'
  import { useForm } from 'vee-validate'
  import { toTypedSchema } from '@vee-validate/zod'
  import { z } from 'zod'
  import { Button, Input, Icon, FormControl, FormField, FormItem, toast } from '@rapoo/element'
  import { useLocaleStore, useUserStore } from '@/stores'
  import { useRouter } from 'vue-router'

  const userStore = useUserStore()
  const { t } = useLocaleStore()
  const router = useRouter()

  const showPassword = ref(false)
  const showConfirmPassword = ref(false)

  const formSchema = toTypedSchema(
    z
      .object({
        username: z
          .string({ message: t('home.user.validate.username') })
          .regex(/^[a-zA-Z0-9@.]+$/, { message: t('home.user.validate.usernameValid') })
          .min(6, { message: t('home.user.validate.usernameValid') }),
        email: z
          .string({ message: t('home.user.validate.email') })
          .email({ message: t('home.user.validate.emailValidFirst') }),
        password: z
          .string({ message: t('home.user.validate.password') })
          .regex(/^[a-zA-Z0-9!?@#￥%&*()]+$/, { message: t('home.user.validate.passwordValid') })
          .min(6, { message: t('home.user.validate.minPassword') })
          .max(20, { message: t('home.user.validate.maxPassword') }),
        rePassword: z
          .string({ message: t('home.user.validate.confirmPassword') })
          .min(1, { message: t('home.user.validate.confirmPassword') }),
      })
      .refine((data) => data.password === data.rePassword, {
        message: t('home.user.validate.passwordNotMatch'),
        path: ['rePassword'],
      }),
  )

  const { handleSubmit, meta } = useForm({
    validationSchema: formSchema,
    initialValues: {
      username: '',
      email: '',
      password: '',
      rePassword: '',
    },
  })

  const onSubmit = handleSubmit((values) => {
    userStore
      .register(values)
      .then(() => {
        toast.success(t('home.user.registerForm.registerSuccessful'))
        router.replace('/')
      })
      .catch((err: any) => {
        toast.error(err.message || t('home.user.registerForm.registerFailed'))
      })
  })
</script>

<template>
  <div>
    <form id="register-form" class="space-y-3" autocomplete="off" @submit="onSubmit">
      <FormField v-slot="{ componentField, errorMessage }" name="username">
        <FormItem>
          <FormControl>
            <Input
              v-bind="componentField"
              :key="'register-username-input'"
              class="h-12 rounded-9xl"
              type="text"
              :placeholder="$t('home.user.usernamePlaceholder')"
              autocomplete="username"
              :error-message="errorMessage"
              :tooltip="$t('home.user.validate.usernameValid')"
            >
              <template #prefix>
                <Icon icon="Function-Wireframe-User" class="size-4" />
              </template>
            </Input>
          </FormControl>
        </FormItem>
      </FormField>

      <FormField v-slot="{ componentField, errorMessage }" name="email">
        <FormItem>
          <FormControl>
            <Input
              v-bind="componentField"
              :key="'register-email-input'"
              class="h-12 rounded-9xl"
              type="text"
              :placeholder="$t('home.user.emailPlaceholder')"
              :error-message="errorMessage"
              autocomplete="email"
            >
              <template #prefix>
                <Icon icon="Multimedia-Wireframe-Mail" class="size-4" />
              </template>
            </Input>
          </FormControl>
        </FormItem>
      </FormField>

      <FormField v-slot="{ componentField, errorMessage }" name="password">
        <FormItem>
          <FormControl>
            <div class="relative items-center">
              <Input
                v-bind="componentField"
                :key="'register-password-input'"
                class="h-12 rounded-9xl"
                :type="showPassword ? 'text' : 'password'"
                :placeholder="$t('home.user.passwordPlaceholder')"
                :error-message="errorMessage"
                :tooltip="$t('home.user.validate.passwordValid')"
                autocomplete="new-password"
              >
                <template #prefix>
                  <Icon icon="Function-Wireframe-Password" class="size-4" />
                </template>
                <template #suffix>
                  <Icon
                    :icon="showPassword ? 'Function-Wireframe-View' : 'Function-Wireframe-Hide'"
                    class="size-4 text-muted-foreground cursor-pointer pointer-events-auto"
                    @click="showPassword = !showPassword"
                  />
                </template>
              </Input>
            </div>
          </FormControl>
        </FormItem>
      </FormField>

      <FormField v-slot="{ componentField, errorMessage }" name="rePassword">
        <FormItem>
          <FormControl>
            <div class="relative items-center">
              <Input
                v-bind="componentField"
                :key="'register-repassword-input'"
                class="h-12 rounded-9xl"
                :type="showConfirmPassword ? 'text' : 'password'"
                :placeholder="$t('home.user.registerForm.confirmPasswordPlaceholder')"
                :error-message="errorMessage"
                autocomplete="new-password"
              >
                <template #prefix>
                  <Icon icon="Function-Wireframe-Password" class="size-4" />
                </template>
                <template #suffix>
                  <Icon
                    :icon="
                      showConfirmPassword ? 'Function-Wireframe-View' : 'Function-Wireframe-Hide'
                    "
                    class="size-4 text-muted-foreground cursor-pointer pointer-events-auto"
                    @click="showConfirmPassword = !showConfirmPassword"
                  />
                </template>
              </Input>
            </div>
          </FormControl>
        </FormItem>
      </FormField>

      <Button color="black" class="w-full h-12 mt-8" :disabled="!meta.valid">
        {{ $t('home.user.registerForm.register') }}
      </Button>
    </form>
  </div>
</template>
